import moment from 'moment-timezone';
import { forEach, get, isEmpty, isNil, uniq } from 'lodash';
import {
  ExpertReportType,
  TvictimExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import {
  EXPERT_REPORT_FIELDS,
  JUDGEMENT,
  PROCEDURE_STATUSES,
  VICTIM,
  VICTIM_EXPERTISE_FIELDS,
} from 'containers/data-structure';
import messages from 'containers/CourtManagerLegalProceedings/ExpertReport/Decision/messages';
import { Claim } from 'containers/PreOpeningPage/typings';
import { MODE } from 'containers/CourtManagerLegalProceedings/ExpertReport/constants';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import { CURRENT_JUDICIARY_STEP_EXPERT_REPORT } from 'containers/CourtManagerLegalProceedings/ExpertReportAppeal/constants';
import { resolveSagisClaim } from 'helpers/utils';
import { STATUS_CONTESTED } from 'containers/LawyerFeeNote/constants';
import {
  COMPTABLE_EXPERTISE_ID,
  MEDICAL_EXPERTISE_ID,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { VictimExpertiseType } from 'containers/ExpertExpertise/ConcernedExpertise/typings';
import { getFirstInstanceVictimExpertises } from 'containers/CourtManagerLegalProceedings/ExpertReportAppeal/utils';
import { getTheLatestEvaluationObject } from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/utils';
import { GLOBAL_STATUS } from 'services/constants';
import { VictimType } from 'typings/globalTypes';

export const getRecentExpert = (
  expertiseReportProps: ExpertReportType,
  expertReport: ExpertReportType,
) => {
  const recentExpertiseReport = {
    ...expertiseReportProps,
    vehiculeExpertises: featureService.hasFeature(FEATURES.EVOLUTION_P1)
      ? get(expertiseReportProps, 'vehiculeExpertises', [])
      : get(expertiseReportProps, 'vehiculeExpertises', []).filter(
          (v: any) => !v.isOld,
        ),
    victimExpertises: featureService.hasFeature(FEATURES.EVOLUTION_P1)
      ? get(expertiseReportProps, 'victimExpertises', [])
      : get(expertiseReportProps, 'victimExpertises', []).filter(
          (v: any) => !v.isOld,
        ),
  };

  const recentExpertReportState = {
    victimExpertises: expertReport.victimExpertises.filter(vi =>
      recentExpertiseReport.victimExpertises.some(v => vi.id === v.id),
    ),
    vehiculeExpertises: expertReport.vehiculeExpertises.filter(vi =>
      recentExpertiseReport.vehiculeExpertises.some(v => vi.id === v.id),
    ),
  };
  return { recentExpertiseReport, recentExpertReportState };
};
export const getExpertReportPayload = (
  expertReportProps: ExpertReportType,
  expertReportState: ExpertReportType,
) => {
  const { recentExpertiseReport, recentExpertReportState } = getRecentExpert(
    expertReportProps,
    expertReportState,
  );
  const {
    victimExpertises,
    vehiculeExpertises,
    ...rest
  } = recentExpertiseReport;

  return {
    ...rest,
    [JUDGEMENT.STATUS]: '',
    expertReport: {
      [EXPERT_REPORT_FIELDS.VEHICULE_EXPERTISES]: get(
        recentExpertReportState,
        'vehiculeExpertises',
        [],
      ).filter(item => item.hasDecision),
      [EXPERT_REPORT_FIELDS.VICTIM_EXPERTISES]: get(
        recentExpertReportState,
        'victimExpertises',
        [],
      ).filter(item => item.hasDecision),
    },
  };
};

export const formatExpertReportStatus = (status: string) => {
  switch (status) {
    case PROCEDURE_STATUSES.CONTESTED:
      return messages.contested;
    case PROCEDURE_STATUSES.VALIDATED:
      return messages.confirmed;
    case PROCEDURE_STATUSES.REJECTED:
      return messages.dismissed;
  }
};

export const mergeEvaluations = (evaluations: any) => {
  const victimEvaluations: any = [];
  const victimKeys = uniq(evaluations.map((el: any) => el?.victimKey));
  forEach(victimKeys, (value: any) => {
    victimEvaluations.push(
      evaluations.filter((el: any) => el?.victimKey === value),
    );
  });
  return victimEvaluations.map((values: any) => {
    const medicalExp = values.find((el: any) => el?.expType === 1);
    const accountingExp = values.find((el: any) => el?.expType === 2);
    if (medicalExp && accountingExp) {
      const hasRecentExpertiseVictim = [medicalExp, accountingExp].find(
        exp => exp.isRecentExpertiseVictim && !exp.isFirstInstance,
      );
      return {
        ...medicalExp,
        responsibilityRate: hasRecentExpertiseVictim
          ? get(hasRecentExpertiseVictim, 'responsibilityRate', '')
          : medicalExp.responsibilityRate,
        revenu: get(accountingExp, '[revenu]', ''),
      };
    } else if (medicalExp && !accountingExp) {
      return { ...medicalExp };
    } else if (!medicalExp && !accountingExp) {
      return { ...values[0] };
    } else {
      return { ...accountingExp };
    }
  });
};

export const resolveResponsabilityShare = (
  responsabilityShare: number | null,
  responsabilityShareClaim: number | null,
) => {
  if (responsabilityShare === null) return responsabilityShareClaim;
  return responsabilityShare;
};
export const getExpertiseSagisEvaluation = (
  expertReportProps: any,
  victimExpertises: TvictimExpertise[],
  victims?: VictimType[],
  claim?: Claim,
  expertiseRaportFirstInstance?: any,
  dahirAmount?: any,
) => {
  const firstInstanceVictimExpertises = getFirstInstanceVictimExpertises(
    victimExpertises,
    get(expertiseRaportFirstInstance, 'victimExpertises', []),
  );
  return [...victimExpertises, ...firstInstanceVictimExpertises]
    .filter(
      vicExp =>
        !vicExp.isOld &&
        vicExp.status !== GLOBAL_STATUS.PENDING &&
        vicExp.status !== GLOBAL_STATUS.REJECTED,
    )
    .map(vicExp => {
      const lastAccountingExpertise =
        get(expertReportProps, 'victimExpertises', []).find((item: any) => {
          return (
            String(item?.victimId) === String(vicExp?.victimId) &&
            !item.isOld &&
            item.status !== GLOBAL_STATUS.PENDING &&
            item.status !== GLOBAL_STATUS.REJECTED &&
            !item.isRejectedJudgement &&
            String(item.expertiseId) === COMPTABLE_EXPERTISE_ID
          );
        }) || {};

      const lastMedicalExpertise =
        get(expertReportProps, 'victimExpertises', []).find(
          (item: any) =>
            String(item?.victimId) === String(vicExp?.victimId) &&
            !item.isOld &&
            item.status !== GLOBAL_STATUS.PENDING &&
            item.status !== GLOBAL_STATUS.REJECTED &&
            !item.isRejectedJudgement &&
            String(item.expertiseId) === MEDICAL_EXPERTISE_ID,
        ) || {};

      const lastEvaluation = getTheLatestEvaluationObject(
        getVictimKey(
          vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
          victims || (([] as unknown) as VictimType[]),
        ),
        dahirAmount,
      );

      return {
        gestionMode: '',
        materialAmount: '',
        currentJudiciaryStep: CURRENT_JUDICIARY_STEP_EXPERT_REPORT,
        judiciaryAmount: '',
        responsibilityRate:
          (vicExp.isDeathRelatedToClaim === null ||
            vicExp.isDeathRelatedToClaim === false) &&
          Number(vicExp.expertiseId) === 1 &&
          vicExp.itt === null
            ? 0
            : resolveResponsabilityShare(
                get(vicExp, 'sharedResponsability', null),
                get(claim, 'responsibilityShare', null),
              ),
        rateRecovery: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL],
          lastEvaluation.rateRecovery,
        ),
        revenu: vicExp[VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME]
          ? vicExp[VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME]
          : !isEmpty(lastAccountingExpertise)
          ? lastAccountingExpertise[VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME]
          : !isNil(
              getVictimRevenu(
                vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                victims || [],
              ),
            )
          ? getVictimRevenu(
              vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
              victims || [],
            )
          : vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE],
        age: getVictimAge(
          vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          victims || [],
          get(claim, 'lossDate', ''),
        ),
        mode: vicExp.injuryPostKey === '2' ? MODE.DECEASED : MODE.BLESSE,
        lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
        ratePretium: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS],
          get(lastEvaluation, 'ratePretium'),
        ),
        rateAestheticDamageWithoutConsequence: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE],
          lastMedicalExpertise,
          lastMedicalExpertise[
            VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE
          ],
          lastEvaluation.rateAestheticDamageWithoutConsequence,
        ),
        rateAestheticDamageWithConsequence: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE],
          lastMedicalExpertise,
          lastMedicalExpertise[
            VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE
          ],
          lastEvaluation.rateAestheticDamageWithConsequence,
        ),
        rateCareerPrejudice: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE],
          lastEvaluation.rateCareerPrejudice,
        ),
        rateSchoolingPrejudice: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE],
          lastEvaluation.rateSchoolingPrejudice,
        ),
        rateIPP: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.IPP],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.IPP],
          lastEvaluation.rateIPP,
        ),
        rateITT:
          Number(vicExp.expertiseId) === 2 && isEmpty(lastMedicalExpertise)
            ? lastEvaluation.rateITT
              ? lastEvaluation.rateITT
              : vicExp.previousItt
            : resolveExpertiseValue(
                vicExp,
                vicExp[VICTIM_EXPERTISE_FIELDS.ITT],
                lastMedicalExpertise,
                lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.ITT],
                lastEvaluation.rateITT,
              ),
        victimKey: getVictimKey(
          vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
          victims || [],
        ),
        medicalPharmaceuticalFees: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES],
          lastMedicalExpertise,
          lastMedicalExpertise[
            VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES
          ],
          lastEvaluation.medicalPharmaceuticalFees,
        ),
        rateMedicalPharmaceuticalFees: '',
        transactionPhase: 1,
        JudiciartDecisionDate: get(vicExp, 'judgementDate', ''),
        expType: Number(vicExp.expertiseId),
        isRecentExpertiseVictim: vicExp.isRecentExpertiseVictim,
      };
    });
};
export const hasDecision = (expertReport: any) => {
  const victimExpertisesWithDecision = get(
    expertReport,
    'expertReport.victimExpertises',
    [],
  ).some((item: VictimExpertiseType) => {
    return (
      item.status === STATUS_CONTESTED ||
      item.status === GLOBAL_STATUS.VALIDATED
    );
  });

  return victimExpertisesWithDecision;
};
export const addEvaluationsToExpertReportPayload = (
  expertReportProps: any,
  expertReport: { expertReport: ExpertReportType },
  victims: VictimType[],
  claim: Claim,
  policyInfo: any,
  dahirAmount: any,
) => {
  const RiskKey = get(resolveSagisClaim(policyInfo), 'offeredRisks', []).find(
    (element: { riskName: string }) => element.riskName === rcCoporeal,
  )?.riskId;

  const provisionBody = {
    riskID: RiskKey,
    date: moment().toISOString(true),
  };

  return {
    ...expertReport,
    sagisEvaluations: mergeEvaluations(
      getExpertiseSagisEvaluation(
        expertReportProps,
        get(expertReport, 'expertReport.victimExpertises', []),
        victims,
        claim,
        {},
        dahirAmount,
      ),
    ),
    provisionBody: hasDecision(expertReport) ? provisionBody : {},
  };
};

export const getVictimAge = (
  itemId: number | null,
  items: any[],
  date: string,
) => {
  const selectedVictim =
    items.find((v: any) => Number(v?.id) === Number(itemId)) || {};
  const dateOfBirth = get(selectedVictim, VICTIM.DATE_OF_BIRTH);
  return Number(
    moment(date)
      .diff(dateOfBirth, 'years', true)
      .toFixed(2),
  );
};

export const getVictimKey = (victimId: string, victims: VictimType[]) => {
  const victim = victims.find(v => Number(v.id) === Number(victimId)) || {};
  return get(victim, VICTIM.VICTIM_KEY);
};
export const getVictimRevenu = (
  victimId: number | null,
  victims: VictimType[],
  judiciaryProcessId?: any,
) => {
  const victim = victims.find(v => Number(v.id) === Number(victimId)) || {};
  const validatedAccountingExpertise = judiciaryProcessId
    ? get(victim, 'victimExpertise', []).find(
        (el: any) =>
          el.expertiseId === 2 &&
          el.status === GLOBAL_STATUS.VALIDATED &&
          String(el?.judgement?.judiciaryProcessId) ===
            String(judiciaryProcessId),
      )
    : get(victim, 'victimExpertise', []).find(
        (el: any) =>
          el.expertiseId === 2 && el.status === GLOBAL_STATUS.VALIDATED,
      ) || {};
  if (
    !isEmpty(get(victim, 'incomeCertificate', {})) &&
    get(victim, 'incomeCertificate.status', {}) === GLOBAL_STATUS.VALIDATED &&
    isEmpty(validatedAccountingExpertise)
  ) {
    return get(victim, VICTIM.ANNUAL_INCOME);
  } else {
    return null;
  }
};

export const mergeEvaluationsV2 = (
  evaluations: any,
  responsabilityShare: any,
) => {
  const victimEvaluations: any = [];
  const victimKeys = uniq(evaluations.map((el: any) => el?.victimKey));
  forEach(victimKeys, (value: any) => {
    victimEvaluations.push(
      evaluations.filter((el: any) => el?.victimKey === value),
    );
  });
  return victimEvaluations.map((values: any) => {
    const medicalExp = values.find((el: any) => el?.expType === 1);
    const accountingExp = values.find((el: any) => el?.expType === 2);
    if (medicalExp && accountingExp) {
      return {
        ...medicalExp,
        responsibilityRate: responsabilityShare,
        revenu: get(accountingExp, '[revenu]', ''),
      };
    } else if (medicalExp && !accountingExp) {
      return { ...medicalExp, responsibilityRate: responsabilityShare };
    } else if (!medicalExp && !accountingExp) {
      return { ...values[0], responsibilityRate: responsabilityShare };
    } else {
      return { ...accountingExp, responsibilityRate: responsabilityShare };
    }
  });
};
export const resolveExpertiseValue = (
  currentExpertise: any,
  value: any,
  expertise: any,
  newValue: any,
  valueSagis?: any,
) => {
  const isFirstInstance = get(currentExpertise, 'isFirstInstance', false);
  const hasValue = value || value === 0;

  if (!isFirstInstance && hasValue) {
    return value;
  }

  if (!isEmpty(expertise)) {
    return newValue;
  }

  if (isFirstInstance && hasValue) {
    return value;
  }

  return String(valueSagis);
};
export const checkExpertiseOnValidation = (
  expertReportState: ExpertReportType,
  expertiseReportProp: ExpertReportType,
) => {
  const { victimExpertises = [], vehiculeExpertises = [] } = expertReportState;
  const validatedVictimExpertiseAfterRejection =
    victimExpertises.find(item =>
      get(expertiseReportProp, 'victimExpertises', []).some(
        el =>
          el.id === item.id &&
          el.status === GLOBAL_STATUS.REJECTED &&
          item.status !== GLOBAL_STATUS.REJECTED,
      ),
    ) || {};
  const validatedVehicleExpertiseAfterRejection =
    vehiculeExpertises.find(item =>
      get(expertiseReportProp, 'victimExpertises', []).some(
        el =>
          el.id === item.id &&
          el.status === GLOBAL_STATUS.REJECTED &&
          item.status !== GLOBAL_STATUS.REJECTED,
      ),
    ) || {};
  return (
    (!isEmpty(validatedVehicleExpertiseAfterRejection) ||
      !isEmpty(validatedVictimExpertiseAfterRejection)) &&
    featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER)
  );
};
